import Tracker from '@openreplay/tracker'
import trackerAssist from '@openreplay/tracker-assist'

const projectKey = import.meta.env.VITE_OPENREPLAY_PROJECT_KEY
const ingestPoint = import.meta.env.VITE_OPENREPLAY_INGEST_POINT
const envName = import.meta.env.VITE_ENV_NAME || 'unknown'
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
const isEnabled = !!(projectKey && ingestPoint && !isLocalhost)

interface UserMetadata {
  email: string
  name?: string
  role?: string
}

let tracker: Tracker | null = null

if (isEnabled) {
  tracker = new Tracker({
    projectKey,
    ingestPoint,
    respectDoNotTrack: false,
    network: {
      // Capture request/response bodies (default false -> "Body not captured")
      capturePayload: true,
      // ignoreHeaders default already redacts Cookie/Set-Cookie/Authorization.
      failuresOnly: false,
      sessionTokenHeader: false,
      ignoreHeaders: ['Cookie', 'Set-Cookie', 'Authorization'],
      captureInIframes: true,
      // Redact credentials before they are persisted to the replay.
      sanitizer: (data) => {
        if (/\/(login|auth|token|refresh)/i.test(data.url)) {
          data.request.body = null
          data.response.body = null
          return data
        }
        const redact = (raw: string | null): string | null => {
          if (typeof raw !== 'string') return raw
          try {
            const obj = JSON.parse(raw)
            for (const key of ['password', 'token', 'accessToken', 'refreshToken']) {
              if (key in obj) obj[key] = '***'
            }
            return JSON.stringify(obj)
          } catch {
            return raw
          }
        }
        data.request.body = redact(data.request.body)
        data.response.body = redact(data.response.body)
        return data
      },
    },
  })
  tracker.use(trackerAssist())
}

export function startTracking(user: UserMetadata) {
  if (!tracker) return
  tracker.start({ forceNew: true }).then(() => {
    tracker?.setUserID(user.email)
    if (user.name) tracker?.setMetadata('name', user.name)
    if (user.role) tracker?.setMetadata('role', user.role)
    tracker?.setMetadata('environment', envName)
    tracker?.forceFlushBatch()
  })
}

export function stopTracking() {
  if (!tracker) return
  tracker.stop()
}

const DIAGNOSTICS_STORAGE_KEY = 'cisgenx:io-diagnostics'
const DIAGNOSTICS_THROTTLE_MS = 1000
const diagnosticsLastSentAt = new Map<string, number>()

const isDiagnosticsEnabled = () => {
  if (import.meta.env.VITE_OPENREPLAY_DIAGNOSTICS === 'true') return true
  try {
    return window.localStorage.getItem(DIAGNOSTICS_STORAGE_KEY) === '1'
  } catch (error) {
    console.error('Failed to read OpenReplay diagnostics flag:', error)
    return false
  }
}

export const trackDiagnostic = (
  name: string,
  payload: Record<string, string | number | boolean | null>,
  throttleKey?: string,
) => {
  if (!isDiagnosticsEnabled()) return

  const loggedAt = new Date().toISOString()
  console.info(`[io-diag] ${loggedAt} ${name}`, payload)

  const sinkUrl = import.meta.env.VITE_IO_DIAG_SINK_URL
  if (sinkUrl) {
    fetch(sinkUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify({ t: loggedAt, name, ...payload }),
      keepalive: true,
    }).catch(error => {
      console.error('Failed to send diagnostics to the local sink:', error)
    })
  }

  if (!tracker) return

  if (throttleKey) {
    const now = Date.now()
    const lastSentAt = diagnosticsLastSentAt.get(throttleKey)
    if (lastSentAt !== undefined && now - lastSentAt < DIAGNOSTICS_THROTTLE_MS) return
    diagnosticsLastSentAt.set(throttleKey, now)
  }

  tracker.event(name, payload)
}
